import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  children: React.ReactNode;
}

interface SelectTriggerProps {
  children: React.ReactNode;
  className?: string;
}

interface SelectValueProps {
  placeholder?: string;
}

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
  /** Abre o menu para cima (útil no rodapé de cards com overflow). */
  side?: 'top' | 'bottom';
}

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

const Select: React.FC<SelectProps> = ({
  value,
  onValueChange,
  placeholder,
  className = '',
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const selectRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** Lista abre em portal (body); cliques nos itões não estão dentro de selectRef. */
  const menuPortalRef = useRef<HTMLDivElement | null>(null);

  const updateMenuPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: r.bottom + 4,
      left: r.left,
      width: r.width,
      zIndex: 9999
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const handlePointerOutside = (event: Event) => {
      const target = event.target as Node;
      if (selectRef.current?.contains(target)) {
        return;
      }
      if (menuPortalRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerOutside);
    return () => {
      document.removeEventListener('pointerdown', handlePointerOutside);
    };
  }, []);

  const handleItemClick = (itemValue: string) => {
    onValueChange(itemValue);
    setIsOpen(false);
  };
 // Encontrar o label do valor selecionado
  const getSelectedLabel = (): string => {
    let selectedLabel = '';
    
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === SelectContent) {
        const contentProps = child.props as SelectContentProps;
        React.Children.forEach(contentProps.children, (item: any) => {
          if (React.isValidElement(item) && item.type === SelectItem) {
            const itemProps = item.props as SelectItemProps;
            if (itemProps.value === value) {
              selectedLabel = itemProps.children as string;
            }
          }
        });
      }
    });
    
    return selectedLabel;
  };

  let contentNode: React.ReactElement | null = null;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === SelectContent) {
      contentNode = child;
    }
  });

  const menuPortal =
    isOpen && contentNode
      ? createPortal(
          React.cloneElement(contentNode as React.ReactElement<any>, {
            onItemClick: handleItemClick,
            value,
            menuStyle,
            menuPortalRef
          }),
          document.body
        )
      : null;

  return (
    <div ref={selectRef} className={`relative ${className}`}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          if (child.type === SelectTrigger) {
            return React.cloneElement(child as any, {
              ref: triggerRef,
              onClick: () => setIsOpen(!isOpen),
              isOpen,
              value,
              placeholder,
              selectedLabel: getSelectedLabel()
            });
          }
          if (child.type === SelectContent) {
            return null;
          }
        }
        return child;
      })}
      {menuPortal}
    </div>
  );
};

const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  SelectTriggerProps & {
    onClick?: () => void;
    isOpen?: boolean;
    value?: string;
    placeholder?: string;
    selectedLabel?: string;
  }
>(({ children, className = '', onClick, isOpen, placeholder, selectedLabel }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={`flex items-center justify-between w-full min-h-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 ${className}`}
    >
      <span
        className={`text-left text-sm truncate pr-2 ${
          selectedLabel ? 'text-gray-900' : 'text-gray-500'
        }`}
      >
        {selectedLabel || placeholder || 'Selecione uma opção'}
      </span>
      <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

const SelectValue: React.FC<SelectValueProps> = ({ placeholder }) => {
  return <span>{placeholder}</span>;
};

const SelectContent: React.FC<
  SelectContentProps & {
    onItemClick?: (value: string) => void;
    value?: string;
    menuStyle?: React.CSSProperties;
    menuPortalRef?: React.MutableRefObject<HTMLDivElement | null>;
  }
> = ({ children, className = '', onItemClick, value, menuStyle, menuPortalRef }) => {
  return (
    <div
      ref={menuPortalRef}
      style={menuStyle}
      className={`bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto ${className}`}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === SelectItem) {
          return React.cloneElement(child as any, {
            onClick: () => onItemClick?.((child.props as any).value),
            isSelected: (child.props as any).value === value
          });
        }
        return child;
      })}
    </div>
  );
};

const SelectItem: React.FC<SelectItemProps & { onClick?: () => void; isSelected?: boolean }> = ({
  value,
  children,
  className = '',
  onClick,
  isSelected
}) => {
  return (
    <button
      type="button"
      onPointerDown={(e) => {
        // Evita que o listener de fora feche o menu antes do toque no mobile
        e.stopPropagation();
      }}
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 min-h-[44px] hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
        isSelected ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
      } ${className}`}
    >
      {children}
    </button>
  );
};

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
export default Select;